import { zodResolver } from '@hookform/resolvers/zod';
import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import type { ProductType, SelectedProduct } from '@shared/components/ui/ProductSelector';
import { Badge } from '@shared/components/ui/badge';
import { NotFound } from '@shared/components/ui/NotFound';
import { Product } from '@shared/components/ui/Product';
import { Alert, AlertDescription } from '@shared/components/ui/shadcn/alert';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useProductsInfinite } from '@shared/hooks/useProductsInfinite';
import { useIsMobile } from '@shared/hooks/useMobile';
import { AlertTriangle, CheckCircle2, Package, XCircle } from 'lucide-react';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { EntryDetailsForm, EntryDetailsSkeleton, ProductForm, ProductFormSkeleton } from '../CreateEdit/components';
import { CreateEntryFormData, createEntrySchema } from '../CreateEdit/models/CreateEntrySchema';
import { ENTRY_STATUS_COLORS, EntryStatus, getEntryStatusLabel } from '../models/Entry';
import { ReceiptsInfo } from './components';
import { useEditEntry, useEntryData } from './hooks';

const ViewEditView: React.FC = memo(() => {
  // Hook de traducción para namespace MerchandiseV3
  const { t } = useTranslation('merchandiseV3');

  // Hook para detectar si estamos en mobile
  const isMobile = useIsMobile();

  // Hook para verificar permisos y tipo de team
  const { teamType } = usePermissions();
  const isTenant3pl = teamType === 'tenant_3pl';

  // Obtener ID de la URL
  const { id } = useParams<{ id: string }>();
  const entryId = parseInt(id || '0', 10);

  // Cargar datos de la entrada existente
  const {
    data: entryData,
    isLoading: isLoadingEntry,
    error: entryError,
  } = useEntryData({
    entryId,
    enabled: !!entryId && entryId > 0,
  });

  // Determinar si la entrada es de solo lectura (no está en estado PENDIENTE)
  const isReadOnly = useMemo(() => {
    return entryData?.status !== 'PENDIENTE';
  }, [entryData?.status]);

  // Estado para productos seleccionados
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([]);

  // Cargar productos desde la API (solo si no es readOnly)
  const { products: apiProducts, isLoading: isLoadingProducts } = useProductsInfinite({
    limit: 1000,
    enabled: !!entryData && !isReadOnly,
    fields: 'basic',
  });

  // Configurar react-hook-form con Zod
  const form = useForm<CreateEntryFormData>({
    resolver: zodResolver(createEntrySchema),
    mode: 'onChange',
    defaultValues: {
      estimated_delivery_at: '',
      boxes_count: null,
      tracking_code: null,
      products: [],
    },
  });

  const {
    handleSubmit,
    setValue,
    trigger,
    reset,
    formState: { errors, isValid },
  } = form;

  // Hook personalizado para manejar el submit de edición
  const { onSubmit, onCancel, isLoading } = useEditEntry({
    entryId,
    form,
    onResetSelectedProducts: useCallback(() => setSelectedProducts([]), []),
  });

  // Ref estable para el submit del header (usePageNavigation ignora cambios en onClick)
  const submitRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    submitRef.current = () => handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  // Efecto para cargar datos iniciales cuando se obtiene la entrada
  useEffect(() => {
    if (entryData) {
      // Establecer valores del formulario
      reset({
        estimated_delivery_at: entryData.estimated_delivery_at || '',
        boxes_count: entryData.boxes_count,
        tracking_code: entryData.tracking_code,
        products: [], // Se llenará cuando se procesen las líneas
      });

      // Convertir líneas de entrada a productos seleccionados
      if (entryData.entry_lines && entryData.entry_lines.length > 0) {
        const initialSelectedProducts: SelectedProduct[] = entryData.entry_lines.map((line) => {
          return {
            id: line.product?.id || line.product_id,
            name: line.product?.name || '',
            code: line.product?.code || '',
            image: line.product?.image_hash || line.product?.img || '',
            price: 0,
            stock: 0,
            quantity: line.expected_qty,
          };
        });

        setSelectedProducts(initialSelectedProducts);
        setValue('products', initialSelectedProducts);
      }
    }
  }, [entryData, reset, setValue]);

  // Convertir productos - SOLO cuando apiProducts cambie REALMENTE
  const availableProducts: ProductType[] = useMemo(() => {
    if (!apiProducts?.length) return [];

    return apiProducts.map((product) => ({
      id: product.id,
      name: product.name,
      code: product.code || '',
      image: product.image_hash || product.image || '',
    }));
  }, [apiProducts]);

  // Handler para cuando cambian los productos seleccionados
  const handleProductsChange = useCallback(
    (products: SelectedProduct[]) => {
      setSelectedProducts(products);
      setValue('products', products);
      trigger('products');
    },
    [setValue, trigger],
  );

  // Helper para obtener el badge de estado
  const getStatusBadge = useCallback(
    (status: EntryStatus) => {
      const label = getEntryStatusLabel(status, t);
      const colorClass = ENTRY_STATUS_COLORS[status] || ENTRY_STATUS_COLORS.PENDIENTE;
      return <Badge className={`${colorClass} text-xs`}>{label}</Badge>;
    },
    [t],
  );

  // Breadcrumbs - dinámicos según dispositivo
  const breadcrumbs = useMemo(() => {
    if (isMobile) {
      // Mobile: Solo mostrar "Ver" o "Editar" según el modo
      return [{ label: isReadOnly ? t('view.breadcrumb', 'Ver entrada') : t('edit.breadcrumb', 'Editar entrada') }];
    }

    // Desktop: Breadcrumbs completos
    return [
      { label: t('breadcrumb.merchandise', 'Mercancía'), path: '/merchandise' },
      {
        label: entryData ? (
          <div className="flex items-center gap-2">
            <span>{entryData.reference}</span>
            {getStatusBadge(entryData.status)}
          </div>
        ) : (
          t('edit.breadcrumb', 'Editar entrada')
        ),
      },
    ];
  }, [entryData, t, isMobile, getStatusBadge, isReadOnly]);

  // Acciones del header - dinámicas según dispositivo y modo
  const headerActions = useMemo(() => {
    // Si es solo lectura, no mostrar botones de edición
    if (isReadOnly) {
      return [];
    }

    if (isMobile) {
      // Mobile: Solo botón de actualizar
      return [
        {
          id: 'update',
          label: isLoading ? t('edit.updating', 'Actualizando...') : t('edit.button', 'Actualizar entrada'),
          onClick: () => submitRef.current?.(),
          variant: 'primary' as const,
          disabled: isLoading || !isValid || isLoadingEntry,
        },
      ];
    }

    // Desktop: Ambos botones
    return [
      {
        id: 'cancel',
        label: t('create.cancel', 'Cancelar'),
        onClick: onCancel,
        variant: 'outline' as const,
      },
      {
        id: 'update',
        label: isLoading ? t('edit.updating', 'Actualizando...') : t('edit.button', 'Actualizar entrada'),
        onClick: () => submitRef.current?.(),
        variant: 'primary' as const,
        disabled: isLoading || !isValid || isLoadingEntry,
      },
    ];
  }, [onCancel, isLoading, isValid, isLoadingEntry, t, isMobile, isReadOnly]);

  // Configurar breadcrumbs y acciones del header
  usePageNavigation({ breadcrumbs, headerActions });

  // Estados de error
  if (entryError) {
    return (
      <NotFound
        entityType={t('entity_type', 'Entrada')}
        entityId={entryId}
        title={t('edit.errors.load_entry_detail', 'Error al cargar la entrada')}
        message={entryError.message || t('edit.errors.entry_not_found', 'Entrada no encontrada')}
        backPath="/merchandise"
        backButtonText={t('edit.errors.back_to_list', 'Volver al listado')}
        testId="entry-edit-error"
      />
    );
  }

  // Estado de carga inicial
  if (isLoadingEntry) {
    return (
      <div className="px-6 pt-4 space-y-8" data-testid="entry-edit-loading">
        <EntryDetailsSkeleton />
        <ProductFormSkeleton />
      </div>
    );
  }

  // Si no se encontró la entrada
  if (!entryData) {
    return (
      <NotFound
        entityType={t('entity_type', 'Entrada')}
        entityId={entryId}
        backPath="/merchandise"
        backButtonText={t('edit.errors.back_to_list', 'Volver al listado')}
        testId="entry-edit-not-found"
      />
    );
  }

  // Vista de solo lectura
  if (isReadOnly) {
    return (
      <div className="px-6 pt-4 space-y-8" data-testid="entry-view">
        {/* Alerta de solo lectura */}
        <Alert className="bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
          <AlertTriangle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          <AlertDescription className="text-sm text-blue-800 dark:text-blue-200">
            {t('view.readonly_alert', 'Esta entrada no puede ser editada porque ya no está en estado pendiente.')}
          </AlertDescription>
        </Alert>

        {/* Sección 1: Información básica (solo lectura) */}
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-8">
          <div className="lg:col-span-2">
            <div className="sticky top-6 space-y-4">
              <div className="space-y-2">
                <h2 className="text-lg font-semibold text-foreground">
                  {t('form.details.title', 'Información de la entrada')}
                </h2>
                <p className="text-sm text-muted-foreground">{t('view.details_description', 'Datos de la entrada')}</p>
              </div>
            </div>
          </div>
          <div className="lg:col-span-4">
            <div className="bg-background border border-border rounded-lg p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">{t('view.reference', 'Referencia')}</p>
                  <p className="font-medium text-foreground">{entryData.reference}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t('view.status', 'Estado')}</p>
                  <div className="mt-1">{getStatusBadge(entryData.status)}</div>
                </div>
                {entryData.estimated_delivery_at && (
                  <div>
                    <p className="text-sm text-muted-foreground">
                      {t('form.details.estimated_delivery_label', 'Fecha estimada de entrega')}
                    </p>
                    <p className="font-medium text-foreground">{entryData.estimated_delivery_at}</p>
                  </div>
                )}
                {entryData.boxes_count && (
                  <div>
                    <p className="text-sm text-muted-foreground">
                      {t('form.details.boxes_count_label', 'Número de cajas')}
                    </p>
                    <p className="font-medium text-foreground">{entryData.boxes_count}</p>
                  </div>
                )}
                {entryData.tracking_code && (
                  <div className="col-span-2">
                    <p className="text-sm text-muted-foreground">
                      {t('form.details.tracking_code_label', 'Código de seguimiento')}
                    </p>
                    <p className="font-medium text-foreground">{entryData.tracking_code}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Sección 2: Productos con cantidades aceptadas/rechazadas */}
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-8">
          <div className="lg:col-span-2">
            <div className="sticky top-6 space-y-4">
              <div className="space-y-2">
                <h2 className="text-lg font-semibold text-foreground">{t('form.products.title', 'Productos')}</h2>
                <p className="text-sm text-muted-foreground">
                  {t('view.products_description', 'Productos de la entrada con cantidades procesadas')}
                </p>
              </div>
            </div>
          </div>
          <div className="lg:col-span-4">
            {entryData.entry_lines && entryData.entry_lines.length > 0 ? (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  {entryData.entry_lines.length} {t('view.products_count', 'productos')}
                </p>
                <div className="border border-border rounded-lg overflow-hidden">
                  {entryData.entry_lines.map((line) => {
                    const qtyAccept = line.qty_accept || 0;
                    const qtyRejects = line.qty_rejects || 0;
                    const qtyPending = line.expected_qty - qtyAccept - qtyRejects;

                    return (
                      <div
                        key={line.id}
                        className="flex items-center justify-between gap-4 p-4 border-b border-border last:border-b-0 bg-background hover:bg-accent/30 transition-colors"
                      >
                        {/* Producto a la izquierda */}
                        <div className="flex-grow min-w-0">
                          <Product
                            id={line.product?.id || line.product_id}
                            name={line.product?.name || `Producto ${line.product_id}`}
                            src={line.product?.image_hash || line.product?.img || ''}
                            code={line.product?.code || ''}
                            showImageModal={true}
                          />
                        </div>

                        {/* Cantidades a la derecha */}
                        <div className="flex items-center gap-4 flex-shrink-0">
                          {/* Cantidad esperada */}
                          <div className="text-center min-w-[70px]">
                            <p className="text-xs text-muted-foreground">{t('view.expected', 'Esperado')}</p>
                            <p className="text-lg font-semibold text-foreground">{line.expected_qty}</p>
                          </div>

                          {/* Cantidad aceptada */}
                          <div className="text-center min-w-[70px]">
                            <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                              <CheckCircle2 className="w-3 h-3 text-green-600" />
                              {t('view.accepted', 'Aceptado')}
                            </p>
                            <p className="text-lg font-semibold text-green-600 dark:text-green-400">{qtyAccept}</p>
                          </div>

                          {/* Cantidad rechazada */}
                          <div className="text-center min-w-[70px]">
                            <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                              <XCircle className="w-3 h-3 text-red-600" />
                              {t('view.rejected', 'Rechazado')}
                            </p>
                            <p className="text-lg font-semibold text-red-600 dark:text-red-400">{qtyRejects}</p>
                          </div>

                          {/* Cantidad pendiente (solo si hay) */}
                          {qtyPending > 0 && (
                            <div className="text-center min-w-[70px]">
                              <p className="text-xs text-muted-foreground">{t('view.pending', 'Pendiente')}</p>
                              <p className="text-lg font-semibold text-orange-600 dark:text-orange-400">{qtyPending}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="border-2 border-dashed border-card-border rounded-lg p-8 text-center">
                <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <h3 className="font-medium">{t('form.products.no_products_selected', 'No hay productos')}</h3>
              </div>
            )}
          </div>
        </div>

        {/* Sección 3: Información de recepciones (solo para tenant_3pl) */}
        {isTenant3pl && entryData && <ReceiptsInfo entry={entryData} />}
      </div>
    );
  }

  // Vista de edición (modo normal)
  return (
    <div className="px-6 pt-4 space-y-8" data-testid="entry-edit-view">
      {/* Formulario con FormProvider */}
      {isLoadingProducts ? (
        <>
          <EntryDetailsSkeleton />
          <ProductFormSkeleton />
        </>
      ) : (
        <>
          <FormProvider {...form}>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
              {/* Sección 1: Información básica */}
              <EntryDetailsForm
                isLoading={isLoading}
                disabled={isLoading}
                title={t('form.details.title', 'Información de la entrada')}
                description={t('form.details.description', 'Completa los datos básicos de la entrada')}
              />

              {/* Sección 2: Selección de productos */}
              <ProductForm
                products={availableProducts}
                initialSelectedProducts={selectedProducts}
                onProductsChange={handleProductsChange}
                isLoading={isLoadingProducts}
                disabled={isLoading}
                allowMultiple={true}
                maxQuantity={999999999}
                title={t('form.products.title', 'Productos')}
                description={t('form.products.description', 'Selecciona los productos de la entrada')}
              />

              {/* Error de productos */}
              {errors.products && (
                <div className="grid grid-cols-1 lg:grid-cols-6 gap-8">
                  <div className="lg:col-span-2"></div>
                  <div className="lg:col-span-4">
                    <p className="text-sm text-destructive" data-testid="products-error">
                      {errors.products.message}
                    </p>
                  </div>
                </div>
              )}
            </form>
          </FormProvider>

          {/* Sección 3: Información de recepciones (solo para tenant_3pl) */}
          {isTenant3pl && entryData && <ReceiptsInfo entry={entryData} />}
        </>
      )}
    </div>
  );
});

ViewEditView.displayName = 'ViewEditView';

export default ViewEditView;
